import React, { useEffect, useState } from "react";
import Header from "../header/Header";
import Word from "../content/word/Word";
import Bottom from "../bottom/Bottom";
import Settings from "../settings/Settings";
import * as S from "./styles";

const GameScreen = ({
  activeWords,
  typingWord,
  matchedWordId,
  health,
  score,
  combo,
  multiplier,
  wave,
  wpm,
  accuracy,
  waveBanner,
  floatingTexts,
  particles,
  healthFlash,
  slowActive,
  freezeActive,
  bombPulse,
  errorPulse,
  paused,
  onRestart,
}) => {
  const matched = activeWords.find((w) => w.id === matchedWordId) || null;
  const [shaking, setShaking] = useState(false);
  const [hurting, setHurting] = useState(false);
  const [bombing, setBombing] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (!errorPulse) return;
    setShaking(true);
    const t = setTimeout(() => setShaking(false), 280);
    return () => clearTimeout(t);
  }, [errorPulse]);

  useEffect(() => {
    if (!healthFlash) return;
    setHurting(true);
    const t = setTimeout(() => setHurting(false), 380);
    return () => clearTimeout(t);
  }, [healthFlash]);

  useEffect(() => {
    if (!bombPulse) return;
    setBombing(true);
    const t = setTimeout(() => setBombing(false), 700);
    return () => clearTimeout(t);
  }, [bombPulse]);

  const classes = ["game"];
  if (shaking) classes.push("shake");
  if (bombing) classes.push("bomb-kick");

  return (
    <div className={classes.join(" ")}>
      <div className="content">
        {activeWords.map((w) => (
          <Word
            key={w.id}
            word={w.text}
            kind={w.kind}
            x={w.x}
            y={w.y}
            isMatched={w.id === matchedWordId}
            typingWord={w.id === matchedWordId ? typingWord : ""}
          />
        ))}
        {particles.map((p) => (
          <S.ParticleBurst key={p.id} style={{ left: `${p.x}%`, top: `${p.y}%` }}>
            {p.pieces.map((pc) => (
              <S.ParticlePiece
                key={pc.id}
                style={{
                  background: p.color,
                  "--dx": `${pc.dx}px`,
                  "--dy": `${pc.dy}px`,
                  "--rot": `${pc.rot}deg`,
                }}
              />
            ))}
          </S.ParticleBurst>
        ))}
        {floatingTexts.map((f) => (
          <S.FloatingText
            key={f.id}
            $variant={f.variant}
            style={{ left: `${f.x}%`, top: `${f.y}%` }}
          >
            {f.text}
          </S.FloatingText>
        ))}
        {waveBanner ? (
          <S.WaveBanner key={waveBanner.id}>
            <div className="label">incoming</div>
            <div className="text">{waveBanner.text}</div>
          </S.WaveBanner>
        ) : null}
      </div>
      <Header
        score={score}
        combo={combo}
        multiplier={multiplier}
        wave={wave}
        wpm={wpm}
        accuracy={accuracy}
      />
      <div style={{ flex: 1 }} />
      <Bottom
        currentWord={matched ? matched.text : ""}
        typingWord={typingWord}
        health={health}
        hurting={hurting}
        onRestart={onRestart}
      />
      {slowActive ? (
        <S.SlowOverlay>
          <S.EffectLabel $variant="slow">SLOW MO</S.EffectLabel>
        </S.SlowOverlay>
      ) : null}
      {freezeActive ? (
        <S.FreezeOverlay>
          {Array.from({ length: 10 }).map((_, i) => (
            <S.Snowflake
              key={i}
              style={{
                left: `${5 + Math.random() * 90}%`,
                top: `${Math.random() * 100}%`,
                "--delay": `${Math.random() * 2}s`,
                "--size": `${3 + Math.random() * 4}px`,
                "--drift": `${(Math.random() - 0.5) * 30}px`,
              }}
            />
          ))}
          <S.FrostCorner $pos="tl" />
          <S.FrostCorner $pos="br" />
          <S.EffectLabel $variant="freeze">FROZEN</S.EffectLabel>
        </S.FreezeOverlay>
      ) : null}
      {paused ? (
        <S.PauseOverlay>
          <S.PauseTitle>PAUSED</S.PauseTitle>
          <S.PauseHint>press <kbd>Tab</kbd> or <kbd>Esc</kbd> to resume</S.PauseHint>
        </S.PauseOverlay>
      ) : null}
      {bombing ? (
        <S.BombOverlay>
          <S.Shockwave />
          <S.Shockwave style={{ animationDelay: "0.08s" }} />
          <S.BombFlash />
        </S.BombOverlay>
      ) : null}
      <div className={shaking ? "error-flash active" : "error-flash"} />
      <div className={hurting ? "hurt-flash active" : "hurt-flash"} />
      {settingsOpen ? <Settings onClose={() => setSettingsOpen(false)} /> : null}
    </div>
  );
};

export default GameScreen;
